import { useCallback } from "react"
import { ASSET_VIEW_EVENTS } from "../analytics/events"

/**
 * Custom hook that provides reusable handlers for asset view analytics.
 * Encapsulates common logic for tracking analytics and updating state for both
 * header interactions and table interactions.
 *
 * @param {Object} params - The parameters object
 * @param {Function} params.track - Analytics track function
 * @param {Function} [params.setSelectedMetricDisplay] - Function to update selected metric display (optional)
 * @param {Function} [params.setSelectedCategory] - Function to update selected category (optional)
 * @returns {Object} Object containing handler functions
 */
export function useAssetViewHeaderHandlers({ track, setSelectedMetricDisplay, setSelectedCategory }) {
  const handleMetricDisplayChange = useCallback((metricDisplay) => {
    track(ASSET_VIEW_EVENTS.DISPLAY_METRIC_SELECTED, { metricDisplay })
    setSelectedMetricDisplay?.(metricDisplay)
  }, [track, setSelectedMetricDisplay])

  const handleCategoryChange = useCallback((category) => {
    if (!category) return

    const normalizedCategory = category.toLowerCase()
    track(ASSET_VIEW_EVENTS.VIEW_SELECTED, { viewName: normalizedCategory })
    setSelectedCategory?.(normalizedCategory)
  }, [track, setSelectedCategory])

  const handleSortChanged = useCallback((sortMetricId, sortDirection) => {
    track(ASSET_VIEW_EVENTS.SORT_CHANGED, { sortMetricId, sortDirection })
  }, [track])

  const handleTableNavigationChanged = useCallback((tableLevels) => {
    track(ASSET_VIEW_EVENTS.TABLE_NAVIGATION_CHANGED, {
      navigationDepth: tableLevels?.length || 0,
      currentLevel: tableLevels?.[tableLevels.length - 1],
    })
  }, [track])

  return {
    handleMetricDisplayChange,
    handleCategoryChange,
    handleSortChanged,
    handleTableNavigationChanged,
  }
}
